import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { iconOptions, renderIconSvg } from './iconsSvg';

const ColorPalette = ({ colors, icons = [], setIcons, selectedColorIdx, setSelectedColorIdx, setColors }) => {
  const [editMode, setEditMode] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [iconMenuAnchor, setIconMenuAnchor] = useState(null);
  const [activeColorIdx, setActiveColorIdx] = useState(null);

  // Debounced color editing state
  const [debouncedColor, setDebouncedColor] = useState(colors[selectedColorIdx] || '#000000');
  useEffect(() => {
    setDebouncedColor(colors[selectedColorIdx] || '#000000');
  }, [selectedColorIdx, colors]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (debouncedColor !== colors[selectedColorIdx]) {
        const newColors = [...colors];
        newColors[selectedColorIdx] = debouncedColor;
        setColors(newColors);
      }
    }, 500); // 500ms debounce
    return () => clearTimeout(handler);
  }, [debouncedColor, selectedColorIdx, colors, setColors]);

  // Debounced color editing for all colors
  const [localColors, setLocalColors] = useState(colors);
  useEffect(() => {
    setLocalColors(colors);
  }, [colors]);

  useEffect(() => {
    const timers = [];
    localColors.forEach((localColor, idx) => {
      if (localColor !== colors[idx]) {
        timers[idx] = setTimeout(() => {
          setColors(prev => {
            if (prev[idx] === localColor) return prev;
            const newColors = [...prev];
            newColors[idx] = localColor;
            return newColors;
          });
        }, 200);
      }
    });
    return () => timers.forEach(timer => timer && clearTimeout(timer));
  }, [localColors, colors, setColors]);

  const handleIconMenuOpen = (event, idx) => {
    event.stopPropagation();
    setIconMenuAnchor(event.currentTarget);
    setActiveColorIdx(idx);
  };

  const handleMenuClose = () => {
    setIconMenuAnchor(null);
    setActiveColorIdx(null);
  };

  const handleIconSelect = (icon) => {
    const newIcons = [...icons];
    newIcons[activeColorIdx] = icon;
    setIcons(newIcons);
    handleMenuClose();
  };

  const handleDeleteColor = (idx, event) => {
    event.stopPropagation();
    if (colors.length <= 1) return; // Don't delete the last color

    const newColors = [...colors];
    const newIcons = [...icons];
    newColors.splice(idx, 1);
    newIcons.splice(idx, 1);

    setColors(newColors);
    setIcons(newIcons);

    if (selectedColorIdx === idx) {
      setSelectedColorIdx(Math.max(0, selectedColorIdx - 1));
    } else if (selectedColorIdx > idx) {
      setSelectedColorIdx(selectedColorIdx - 1);
    }

    if (editingIdx === idx) setEditingIdx(null);
  };

  const addNewColor = () => {
    const newColor = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
    setColors([...colors, newColor]);
    setIcons([...icons, '']);
    setSelectedColorIdx(colors.length);
  };

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle2">Color Palette</Typography>
        <Button variant={editMode ? 'contained' : 'outlined'}
          size="small" startIcon={<EditIcon fontSize="small" />}
          onClick={() => {
            setEditMode(!editMode);
            setEditingIdx(null);
            handleMenuClose();
          }}
          color={editMode ? 'primary' : 'inherit'}
        >
          {editMode ? 'Done Editing' : 'Edit Colors'}
        </Button>
      </Box>

      {/* VIEW MODE SECTION */}
      {!editMode && (
        <Box sx={{
          display: 'flex',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          p: 1,
        }}>
          {colors.map((color, idx) => {
            const icon = icons[idx] || '';
            const isSelected = selectedColorIdx === idx;
            return (
              <Box
                key={`${color}-${idx}`}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                }}
                onClick={() => setSelectedColorIdx(idx)}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: color,
                    border: isSelected
                      ? '3px solid #1976d2'
                      : '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isSelected
                      ? '0 0 0 3px rgba(25, 118, 210, 0.2)'
                      : 'none',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {icon && (
                    <Box
                      component="span"
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <svg width={24} height={24} viewBox="0 0 10 10" style={{ display: 'block' }}>
                        {renderIconSvg(icon, '#ffffff', false)}
                      </svg>
                    </Box>
                  )}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace', userSelect: 'none', }}>
                  {color.toUpperCase()}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* EDIT MODE SECTION */}
      {editMode && (
        <Box sx={{
          display: 'flex',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          p: 1,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}>
          {localColors.map((color, idx) => {
            return (
              <Box
                key={idx}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: 'transparent',
                }}
              >
                <input
                  type="color"
                  value={color}
                  style={{ width: '36px', height: '36px', border: 'none', cursor: 'pointer', borderRadius: '8px', padding: 0 }}
                  onChange={e => {
                    const newLocalColors = [...localColors];
                    newLocalColors[idx] = e.target.value;
                    setLocalColors(newLocalColors);
                  }}
                />
                <IconButton
                  size="small"
                  onClick={e => handleIconMenuOpen(e, idx)}
                  sx={{ borderRadius: 1 }}
                  aria-label="Change icon"
                >
                  {icons[idx] ? (
                    <svg width={20} height={20} viewBox="0 0 10 10">
                      {renderIconSvg(icons[idx], 'grey', false)}
                    </svg>
                  ) : (
                    <Box sx={{ width: 20, height: 20 }} >-</Box>
                  )}
                </IconButton>
                {colors.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={e => handleDeleteColor(idx, e)}
                    sx={{ borderRadius: 1 }}
                    aria-label="Remove color"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          })}
          <IconButton
            size="small"
            onClick={addNewColor}
            aria-label="Add new color"
            variant="outlined"
            sx={{
              width: 36,
              height: 36,
            }}
          >
            <AddIcon />
          </IconButton>
          <input
            type="color"
            value={colors[1]}
            style={{ width: '36px', height: '36px', border: 'none', cursor: 'pointer', borderRadius: '8px', padding: 0 }}
            onChange={e => {
              const newColors = [...colors];
              newColors[1] = e.target.value;
              setColors(newColors);
            }}
          />
        </Box>
      )}

      {/* Icon selection menu */}
      <Menu
        anchorEl={iconMenuAnchor}
        open={Boolean(iconMenuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxHeight: 300,
            width: 200,
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, pt: 1, pb: 0.5, color: 'text.secondary' }}>
          Select Icon
        </Typography>
        <MenuItem
          onClick={() => handleIconSelect('')}
          selected={!iconMenuAnchor ? false : !icons[activeColorIdx]}
          sx={{
            minHeight: 36,
            '&.Mui-selected': {
              bgcolor: 'action.selected',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
          }}
        >
          <Box sx={{ width: 24, height: 24, mr: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 16, height: 2, bgcolor: 'text.disabled' }} />
          </Box>
          <Typography variant="body2">None</Typography>
        </MenuItem>
        {iconOptions.filter(opt => opt.value).map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => handleIconSelect(opt.value)}
            selected={iconMenuAnchor ? icons[activeColorIdx] === opt.value : false}
            sx={{
              minHeight: 36,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
            }}
          >
            <Box sx={{
              width: 24,
              height: 24,
              mr: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width={16} height={16} viewBox="0 0 10 10">
                {renderIconSvg(
                  opt.value,
                  'grey',
                  false
                )}
              </svg>
            </Box>
            <Typography variant="body2">{opt.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <Typography
        variant="body2"
        sx={{
          mb: 1,
          color: 'text.secondary',
          fontSize: '0.75rem',
          lineHeight: 1.4,
          fontStyle: 'italic',
        }}
      >
        {editMode
          ? '• Click a color to change it • Use icons to distinguish similar colors • Drag to reorder'
          : 'Click a color to select it, then click on the chart to apply'}
      </Typography>
    </Box>
  );
};

export default ColorPalette;
